import React, { useState, useEffect, useMemo } from 'react';
import PlotlyChart from '../../components/charts/PlotlyChart';
import { hfService } from '../../services/hfService';
import { parseDfJson, formatPct, formatDate } from '../../utils/formatters';
import { getFundColor } from '../../utils/colors';

const DrawdownPage = ({ analyticsParams, focusFund, cachedFetch }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'focus'

  useEffect(() => {
    if (!analyticsParams?.dataset_id || !analyticsParams?.funds?.length) return;

    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchFn = () => hfService.getDrawdown(analyticsParams);
        const res = cachedFetch ? await cachedFetch('drawdown', fetchFn) : await fetchFn();
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [analyticsParams, focusFund]);

  // Focus fund from parent

  // Filter funds based on view mode
  const displayFunds = useMemo(() => {
    if (!data) return [];
    const dd = parseDfJson(data.drawdown);
    const allFunds = dd.columns;
    if (viewMode === 'focus' && focusFund) {
      return allFunds.filter(f => f === focusFund);
    }
    return allFunds;
  }, [data, viewMode, focusFund]);

  if (!analyticsParams?.dataset_id) {
    return (
      <div className="rounded-lg border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-3 text-sm text-blue-700 dark:text-blue-400">
        Select a dataset and funds to view drawdown analysis.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-brand-gold" />
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Calculating drawdowns...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
        {error}
      </div>
    );
  }

  if (!data) return null;

  const { drawdown, max_drawdown, episodes, colors, spx_drawdown } = data;
  const dd = parseDfJson(drawdown);

  /* --- Drawdown time-series area chart ---
   * Start from focus fund's inception for ALL funds.
   * Each drawdown re-rebased from that date onwards.
   */
  // Find focus fund's inception index (first non-null)
  let focusStartIdx = 0;
  if (focusFund) {
    const fi = dd.columns.indexOf(focusFund);
    if (fi >= 0) {
      const idx = dd.data.findIndex(row => row[fi] != null);
      if (idx > 0) focusStartIdx = idx;
    }
  }

  // Filter null y-values so Plotly auto-scales
  const stripNulls = (xArr, yArr) => {
    const acc = { x: [], y: [] };
    for (let i = 0; i < xArr.length; i++) {
      if (yArr[i] != null && !isNaN(yArr[i])) { acc.x.push(xArr[i]); acc.y.push(yArr[i]); }
    }
    return acc;
  };

  const ddTraces = displayFunds.map((fund) => {
    const ci = dd.columns.indexOf(fund);
    // Slice from focus inception, then rebase drawdown from that point's peak
    const sliceX = dd.index.slice(focusStartIdx);
    const sliceY = dd.data.slice(focusStartIdx).map(row => row[ci]);
    const { x, y } = stripNulls(sliceX, sliceY);
    return {
      x, y, name: fund,
      type: 'scatter', mode: 'lines', fill: 'tozeroy',
      line: { color: getFundColor(fund, colors), width: 1.5 },
      fillcolor: getFundColor(fund, colors) + '30',
      hovertemplate: `%{x|%b %Y}<br>${fund}: %{y:.2%}<extra></extra>`,
    };
  });

  // SPX drawdown — also sliced
  if (spx_drawdown) {
    const spxDd = parseDfJson(spx_drawdown);
    if (spxDd.index.length > 0) {
      // Align SPX to focus fund start date
      const focusStartDate = dd.index[focusStartIdx];
      const spxStartIdx = focusFund ? spxDd.index.findIndex(d => d >= focusStartDate) : 0;
      const ssIdx = spxStartIdx < 0 ? 0 : spxStartIdx;
      const sliceX = spxDd.index.slice(ssIdx);
      const sliceY = spxDd.data.slice(ssIdx).map(row => (Array.isArray(row) ? row[0] : row));
      const { x, y } = stripNulls(sliceX, sliceY);
      if (x.length > 0) {
        ddTraces.push({
          x, y, name: 'SPX Index',
          type: 'scatter', mode: 'lines',
          line: { color: '#DC143C', width: 2, dash: 'dash' },
          hovertemplate: '%{x|%b %Y}<br>SPX: %{y:.2%}<extra></extra>',
        });
      }
    }
  }

  /* --- Max drawdown bar chart (filtered + SPX) --- */
  const ddFunds = viewMode === 'focus' && focusFund
    ? [focusFund, ...(max_drawdown['SPX Index'] != null ? ['SPX Index'] : [])]
    : [...displayFunds, ...(max_drawdown['SPX Index'] != null ? ['SPX Index'] : [])];

  const sortedFunds = ddFunds
    .filter(f => max_drawdown[f] != null)
    .sort((a, b) => max_drawdown[a] - max_drawdown[b]);

  const maxDdTrace = {
    x: sortedFunds.map(f => max_drawdown[f]),
    y: sortedFunds,
    type: 'bar',
    orientation: 'h',
    marker: {
      color: sortedFunds.map(f => f === 'SPX Index' ? '#DC143C' : getFundColor(f, colors)),
    },
    text: sortedFunds.map(f => formatPct(max_drawdown[f])),
    textposition: 'auto',
    hovertemplate: '%{y}: %{x:.2%}<extra></extra>',
  };

  /* --- Median of worst 5 drawdowns bar chart --- */
  const medianWorst5 = {};
  sortedFunds.forEach(fund => {
    const eps = (episodes[fund] || []).slice(0, 5);
    if (eps.length > 0) {
      const ddValues = eps.map(e => e.Depth).filter(v => v != null).sort((a, b) => a - b);
      if (ddValues.length > 0) {
        // median of sorted values
        const mid = Math.floor(ddValues.length / 2);
        medianWorst5[fund] = ddValues.length % 2 === 0
          ? (ddValues[mid - 1] + ddValues[mid]) / 2
          : ddValues[mid];
      }
    }
  });
  const medianTrace = {
    x: sortedFunds.map(f => medianWorst5[f] ?? null),
    y: sortedFunds,
    type: 'bar',
    orientation: 'h',
    marker: {
      color: sortedFunds.map(f => f === 'SPX Index' ? '#DC143C' : getFundColor(f, colors)),
    },
    text: sortedFunds.map(f => medianWorst5[f] != null ? formatPct(medianWorst5[f]) : ''),
    textposition: 'auto',
    hovertemplate: '%{y}: %{x:.2%}<extra></extra>',
  };

  /* --- Episodes — show focus fund + SPX, or all funds + SPX --- */
  const episodeFunds = viewMode === 'focus' && focusFund
    ? [focusFund, ...(episodes['SPX Index']?.length ? ['SPX Index'] : [])]
    : [...displayFunds, ...(episodes['SPX Index']?.length ? ['SPX Index'] : [])];

  return (
    <div>
      {/* View toggle */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setViewMode('all')}
          className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
            viewMode === 'all'
              ? 'bg-brand-gold text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          All in Strategy
        </button>
        <button
          onClick={() => setViewMode('focus')}
          className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
            viewMode === 'focus'
              ? 'bg-brand-gold text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          {focusFund ? `${focusFund} + SPX` : 'Focus Fund + SPX'}
        </button>
      </div>

      {/* Drawdown time series */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Drawdown Over Time</h3>
        <PlotlyChart data={ddTraces} layout={{
          yaxis: { title: 'Drawdown', tickformat: '.0%' },
          xaxis: { title: '' },
        }} style={{ minHeight: 350 }} />
      </div>

      {/* Drawdown bar charts — median worst 5 | max drawdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Median of Worst 5 Drawdowns</h3>
          <PlotlyChart
            data={[medianTrace]}
            layout={{
              xaxis: { title: 'Median Drawdown', tickformat: '.0%' },
              margin: { l: 180, r: 15, t: 10, b: 40 },
              showlegend: false,
            }}
            style={{ minHeight: Math.max(250, sortedFunds.length * 40) }}
          />
        </div>
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Maximum Drawdown Comparison</h3>
          <PlotlyChart
            data={[maxDdTrace]}
            layout={{
              xaxis: { title: 'Max Drawdown', tickformat: '.0%' },
              margin: { l: 180, r: 15, t: 10, b: 40 },
              showlegend: false,
            }}
            style={{ minHeight: Math.max(250, sortedFunds.length * 40) }}
          />
        </div>
      </div>

      {/* Worst drawdown episodes per fund (+ SPX) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {episodeFunds.map(fund => {
          const eps = (episodes[fund] || []).slice(0, 5);
          if (!eps.length) return null;
          const isSpx = fund === 'SPX Index';
          return (
            <div key={fund} className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
              <div
                className="px-4 py-3 font-semibold text-sm border-b border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-800/40"
                style={{ borderLeft: `4px solid ${isSpx ? '#DC143C' : getFundColor(fund, colors)}` }}
              >
                <span className={isSpx ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-200'}>
                  {fund} — Worst Drawdowns
                </span>
              </div>
              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-sm">
                  <thead className="bg-gray-200/70 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-700 dark:text-gray-500">
                    <tr>
                      <th className="px-3 py-2.5 text-left">#</th>
                      <th className="px-3 py-2.5 text-left">Start</th>
                      <th className="px-3 py-2.5 text-left">Trough</th>
                      <th className="px-3 py-2.5 text-left">End</th>
                      <th className="px-3 py-2.5 text-right">Depth</th>
                      <th className="px-3 py-2.5 text-right">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {eps.map((ep, i) => (
                      <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300">{i + 1}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{formatDate(ep.Start)}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{formatDate(ep.Trough)}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{formatDate(ep.End) || 'Ongoing'}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-red-500">
                          {formatPct(ep.Depth)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                          {ep.Duration != null ? `${ep.Duration}m` : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DrawdownPage;
